import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { priceForecastService } from "@/services/priceForecastService";
import { Loader2, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart } from "recharts";
import { format } from "date-fns";
import { getCanonicalProductNames } from "@/services/benchmark/productNameMapping";

export default function ForecastVisualization() {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [forecastHorizon, setForecastHorizon] = useState("28");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forecasts, setForecasts] = useState<any[]>([]);
  const { toast } = useToast();
  const canonicalProducts = getCanonicalProductNames().sort();

  const handleGenerate = async () => {
    if (!selectedProduct) {
      toast({
        title: "Product Required",
        description: "Please select a product",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    try {
      await priceForecastService.generateForecasts(
        selectedProduct,
        parseInt(forecastHorizon)
      );

      toast({
        title: "Forecasts Generated",
        description: "Price predictions have been generated successfully",
      });

      // Reload forecasts
      loadForecasts();
    } catch (error: any) {
      console.error('Generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate forecasts",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const loadForecasts = async () => {
    if (!selectedProduct) return;

    setIsLoading(true);
    try {
      const data = await priceForecastService.getForecasts({
        product_name: selectedProduct,
        date_from: format(new Date(), 'yyyy-MM-dd'),
      });

      setForecasts(data);
    } catch (error) {
      console.error('Error loading forecasts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      loadForecasts();
    }
  }, [selectedProduct]);

  // Transform data for chart
  const chartData = forecasts.reduce((acc: any[], forecast) => {
    const existingDate = acc.find(d => d.date === forecast.forecast_date);
    
    if (existingDate) {
      existingDate[forecast.location_group] = forecast.predicted_price;
      existingDate[`${forecast.location_group}_lower`] = forecast.confidence_lower;
      existingDate[`${forecast.location_group}_upper`] = forecast.confidence_upper;
    } else {
      acc.push({
        date: forecast.forecast_date,
        [forecast.location_group]: forecast.predicted_price,
        [`${forecast.location_group}_lower`]: forecast.confidence_lower,
        [`${forecast.location_group}_upper`]: forecast.confidence_upper,
      });
    }
    
    return acc;
  }, []);

  const locationGroups = [...new Set(forecasts.map(f => f.location_group))];
  const colors = ['hsl(var(--primary))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Generate Forecasts
          </CardTitle>
          <CardDescription>
            Generate price predictions for the next 7-28 days
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {canonicalProducts.map(product => (
                    <SelectItem key={product} value={product}>
                      {product}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Forecast Horizon</Label>
              <Select value={forecastHorizon} onValueChange={setForecastHorizon}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="21">21 days</SelectItem>
                  <SelectItem value="28">28 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !selectedProduct}
            className="w-full"
          >
            {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isGenerating ? "Generating..." : "Generate Forecasts"}
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : chartData.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Price Forecasts - {selectedProduct}</CardTitle>
            <CardDescription>
              Predicted prices across different markets
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: 'hsl(var(--foreground))' }}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis tick={{ fill: 'hsl(var(--foreground))' }} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                {locationGroups.map((location, index) => (
                  <Line
                    key={location}
                    type="monotone"
                    dataKey={location}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={location}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : selectedProduct ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-96 text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No forecasts available for {selectedProduct}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Generate forecasts to see predictions
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
